// MapVehRoute.js
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Box } from '@mui/system';
import { Button, IconButton, Popover, CircularProgress } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useAlertSetter } from '../../api/app-hook';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import FlexyTable from '../../common/flexytable/FlexyTable';
import ValueMisc from '../../value/ValueMisc';
import { parseMidRoute, VehRouteDictionary } from '../veh_route/VehRouteDict';
import DurationPicker from '../../common/DurationPicker';
import { getFloatingInfoBoxHeight } from './MapFloatingInfo';
import { LIST_VEH_ROUTE } from '../veh_route/VehRouteGql';

const VehRouteListContainer = styled(Box)({
    flexGrow:1,
	display:'flex', flexDirection:'column'
});

const HeaderBox = styled(Box)({
    display:'flex', 
    alignItems: 'center', padding: 10,
    // '&>*': {verticalAlign: 'middle'}
});

const TableBox = styled(Box)({flexGrow:1, display:'flex'});

const DurationView = styled(Box)({
    display:'inline-block',fontFamily: 'Consolas, Monaco, Courier, monospace', fontSize:'0.9em'
});

const PickerBox = styled(Box)({
    display:'flex', alignItems:'center', padding:10
});

const FloatingInfoOption = {
    width: 520, // box width
    // rowHeight: 40,
};
const FloatingInfoProps = VehRouteDictionary.floatingInfoProps;

// TODO : 향후 이 함수의 알고리즘을 경로표시에 적용해야 함. (일부 마커만 표시하여 성능 향상)
// 예전 프로젝트(map21)에 적용해 보았음.
// 결론은 로직의 문제라기 보다는 운영체제의 문제처럼 보였음.
// 맥에서는 로직과 관계없이 빠른 응답을 보였으나,
// 윈도우에서는 첫번째 마커가 나타나기까지 1분 넘는 시간이 걸리면서 (대기/나가기) 묻기가 계속 나타남.
// 맥에서는 크롬, 사파리를, 윈도우에서는 크롬, 엣지를 사용하여 테스트했으며, 브라우저에 따른 차이는 느낄 수 없었음.
function getExactlyLimitedMarkers(markers, limit) {
    if(markers.length <= limit) return markers;
    const step = markers.length / limit; // It could be a float.
    const newMarkers = [];
    let lastIndex = -1;
    for(let i=0; i<limit; i++) {
        if(i===0) {
            newMarkers.push(markers[0]);
            lastIndex = 0;
        }
        else {
            const idx = Math.round(i*step);
            if(idx > lastIndex) {
                newMarkers.push(markers[idx]);
                lastIndex = idx;
            }
        }
    }
    return newMarkers;
}

const EmptyDuration = {from:null, till:null};

// 경로
export default function MapVehRoute({
    mapShell,
    vehPos,
    onMouseEnterIndexCell,
}) {
    const [route, setRoute] = useState([]);
    const [duration, setDuration] = useState(EmptyDuration);
    const [selectorAnchor, setSelectorAnchor] = useState(null);
    const [itemSelected, setItemSelected] = useState(null);
    //const [responseAlert, setResponseAlert] = useState(null);
    const addAlert = useAlertSetter();

    const floatingInfoBoxHeight = getFloatingInfoBoxHeight(FloatingInfoProps, FloatingInfoOption);

    // ##### Call GraphQL to get List #####
    const [getVehRouteList, responseList] = useLazyQuery(LIST_VEH_ROUTE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
        onError: (error) => addAlert({title: '경로조회 오류', message: [error.message]})
    });

    useEffect(()=>{
        setRoute([]);
        if(vehPos) {
            if(vehPos.gpsTime) {
                if(vehPos.gpsTime === AppWord.NOT_REAL_GPS_TIME) setDuration(EmptyDuration);
                else {
                    const djs = dayjs(vehPos.gpsTime);
                    const date = djs.toDate();
                    const hour = date.getHours();
                    if(hour > 12) date.setHours(hour-12);
                    else if(hour <= 6) {
                        date.setTime(date.getTime() - 6*3600*1000);
                    }
                    else {
                        date.setHours(0);
                        date.setMinutes(0);
                        date.setSeconds(0);
                    }
                    const from = ValueMisc.getYmdHms(date);
                    setDuration({from:from, till: vehPos.gpsTime});
                }
            }
            else setDuration(EmptyDuration);
        }
        else setDuration(EmptyDuration);
    }, [vehPos]);

    const onMouseEnterTableIndexCell = (item, e) => {
        if(!item) onMouseEnterIndexCell(null);
        else {
            const windowHeight = window.innerHeight - 10;
            let y = e.clientY; // + AppNumber.MapHeaderHeight;
            if(y + floatingInfoBoxHeight > windowHeight) {
                y = windowHeight - floatingInfoBoxHeight;
            }
            onMouseEnterIndexCell(item, FloatingInfoProps, FloatingInfoOption, y);
        }
    };

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        let gpsTimeSel = itemSelected ? itemSelected.gpsTime : undefined;

        if(data.vehRouteList) {
            const newRoute = [];
            const staySpot = {};

            let lastArr = '1000-', lastDep = '1000-', lastStay=0, lastBetween=0, lastTime=null;
            let sdist = 0;
            let preDist = 0;
            let i = 0;

            for(const p of data.vehRouteList) {
                if(p.midRoute) {
                    p.midPoints = parseMidRoute(p.midRoute);
                }

                if(p.distKm > preDist) {
                    if(i>0) {
                        sdist += (p.distKm - preDist);
                    }
                    preDist = p.distKm;
                }
                p.sessionDist = sdist;
                if(p.distKm > 0) i++;

                // 동정정보 반영.
                if(p.timeArrival && p.timeDeparture) {
                    lastArr = p.timeArrival;
                    lastDep = p.timeDeparture;
                    lastStay = p.staySec;
                    lastBetween = p.distBetween;
                    lastTime = p.gpsTime;
                    staySpot[lastTime] = {east:p.lon+0.0000001, west:p.lon-0.0000001, north:p.lat+0.0000001, south:p.lat-0.0000001};
                }
                else {
                    if(lastArr < p.gpsTime && lastDep > p.gpsTime && lastTime) {
                        p.timeArrival = lastArr;
                        p.timeDeparture = lastDep;
                        p.staySec = lastStay;
                        p.distBetween = lastBetween;
                        if(staySpot[lastTime].east < p.lon) staySpot[lastTime].east = p.lon;
                        if(staySpot[lastTime].west > p.lon) staySpot[lastTime].west = p.lon;
                        if(staySpot[lastTime].north < p.lat) staySpot[lastTime].north = p.lat;
                        if(staySpot[lastTime].south > p.lat) staySpot[lastTime].south = p.lat;
                    }
                    else lastTime = null;
                }

                newRoute.push(p);

                if(gpsTimeSel) {
                    setItemSelected(p);
                    gpsTimeSel = undefined;
                }
            }

            for(let i=0;i<newRoute.length;i++) {
                if(staySpot[newRoute[i].gpsTime]) {
                    newRoute[i].stayBox = staySpot[newRoute[i].gpsTime];
                }
            }

            if(newRoute.length>0) {
                mapShell.setVehicleRoute(vehPos, newRoute); // reverse 하기 전에 설정해야 함.
                mapShell.showRouteMarkers();
            }

            setRoute(newRoute.reverse());
        }
    };

    const onClickToGetList = () => {
        setSelectorAnchor(null);
        if(vehPos.vehInfoSeq && duration.from && duration.till) {
            getVehRouteList({
                variables: {vehInfoSeq: vehPos.vehInfoSeq, timeFrom: duration.from, timeTill: duration.till}
            });
        }
    };

    const onSelectItem = (item) => {
        setItemSelected(item);
        mapShell.morphTo(item.lat, item.lon);
        mapShell.showRoutePointTimeMarker(
            item.lat, item.lon,
            {text: item.gpsTime}
        );
    };

    const onChangeFrom = (from) => {
        const dur = {...duration};
        dur.from = from;
        setDuration(dur);
    };

    const onChangeTill = (till) => {
        const dur = {...duration};
        dur.till = till;
        setDuration(dur);
    };

    const onCloseSelector = () => {
        setSelectorAnchor(null);
    };

    return(
        <VehRouteListContainer>
            <HeaderBox>
                <IconButton
                    color={AppStyle.mui.primary}
                    onClick={(e)=>setSelectorAnchor(e.currentTarget)}
                >
                    <CalendarMonthIcon/>
                </IconButton>
                <Popover
                    open={Boolean(selectorAnchor)}
                    anchorEl={selectorAnchor}
                    onClose={onCloseSelector}
                    anchorOrigin={{ vertical: AppWord.TOP, horizontal: AppWord.LEFT, }}
                    transformOrigin={{ vertical: AppWord.TOP, horizontal: AppWord.LEFT, }}
                >
                    <PickerBox>
                        <DurationPicker
                            from={duration.from} onChangeFrom={onChangeFrom}
                            till={duration.till} onChangeTill={onChangeTill}
                        />
                        <Button
                            size={AppWord.SMALL}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            disabled={!Boolean(duration) || !Boolean(vehPos)}
                            onClick={onClickToGetList}
                            style={{marginLeft:10, marginRight:10}}
                        >
                            조회
                        </Button>
                    </PickerBox>
                </Popover>
                {
                    duration.from && duration.till
                    ?
                    <DurationView>
                        <Box>{duration.from.substr(0,16)} 에서</Box>
                        <Box>{duration.till.substr(0,16)} 까지</Box>
                    </DurationView>
                    :
                    <span>기간을 선택하세요</span>
                }
                {
                    responseList.loading ?
                    <CircularProgress size={20} />
                    :
                    duration.from && duration.till && route.length===0
                    ?
                    <Button size={AppWord.SMALL}
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.primary}
                        onClick={onClickToGetList}
                        style={{marginLeft:10}}
                    >
                        조회
                    </Button>
                    : null
                }
            </HeaderBox>
            <TableBox>
                <FlexyTable
                    name={VehRouteDictionary.tablePropsForMap.tableId}
                    tableProps={VehRouteDictionary.tablePropsForMap}
                    records={route}
                    tools={[]}
                    onRowClicked={onSelectItem}
                    selected={itemSelected ? [itemSelected] : null}
                    onEnterIndex={onMouseEnterTableIndexCell}
                />
            </TableBox>
        </VehRouteListContainer>
    );

}
